import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { ANALYTICS_EVENTS } from '../../../../core/analytics/events';
import { enterMotion, leaveMotion } from '../../../../core/motion';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { DelegationService } from '../../../../core/services/delegation.service';
import { ShellStateService } from '../../../../core/services/shell-state.service';
import { getInitials } from '../../../../core/utils/text';
import { DelegatePickerService } from '../../../../shared/components/delegate-picker-dialog/delegate-picker.service';
import { PreviewIconComponent } from '../../../../shared/preview-icon/preview-icon.component';
import { HintDirective } from '../../../../shared/workspace-kit/hover-menus';

/** The search the pill hangs from: Louis's nav's, or the Paper bar's. */
const SEARCH = 'im-soft-frost-rail .frost-search, paper-shell-bar paper-search-pill';

/**
 * `im-delegation-pill` — booking on someone's behalf, said by a floating
 * pill at the top centre, under the search, instead of the grey strip that
 * pushed the whole app down (Nath, 5 October 2026: "this could be a much more
 * beautiful floating UI, maybe in the middle, overlapping search").
 *
 * WHAT IT SHOWS while `DelegationService` is active: the person's photo (or
 * initials), "Booking for {name}", a switch to book for someone else (the
 * same picker the map's control opens) and Exit, which ends the mode as the
 * strip's did. It takes no room: a glass control floating over the page
 * (decision 20), app-wide, so nothing moves when the mode starts or ends.
 *
 * MOTION. It drops out of the search into place and recedes back towards it
 * (core/motion's enter and leave). With motion reduced it is simply there.
 *
 * ON A PHONE it is compact: the face and the name, the switch and Exit as
 * glyphs (their names stay on them for a screen reader).
 *
 * ANALYTICS: `emp.shell.delegation.pill_action` (switch, exit), with the
 * section. The switch also records the picker's own events as it always has.
 */
@Component({
  selector: 'im-delegation-pill',
  standalone: true,
  imports: [TranslocoPipe, PreviewIconComponent, HintDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ph-no-capture' },
  template: `
    @if (shown(); as person) {
      <div class="pill" data-testid="delegation-banner" role="status" aria-live="polite"
        (animate.enter)="arrive($event)" (animate.leave)="recede($event)">
        <span class="face" aria-hidden="true">
          @if (person.avatarUrl) { <img [src]="person.avatarUrl" alt="" /> } @else { {{ initials() }} }
        </span>
        <span class="words" ngPreserveWhitespaces><span class="lead">{{ 'shell.delegation.booking_for' | transloco }}</span> <strong class="name ph-mask">{{ person.name }}</strong></span>
        <span class="rule" aria-hidden="true"></span>
        <button type="button" class="act switch" data-testid="delegation-switch"
          [attr.aria-label]="'shell.delegation.switch' | transloco" [imHint]="'shell.delegation.switch' | transloco"
          (click)="switchPerson()">
          <im-preview-icon name="people" [size]="16" />
        </button>
        <button type="button" class="act exit" data-testid="delegation-exit"
          [attr.aria-label]="'shell.delegation.exit_aria' | transloco: { name: person.name }" (click)="exit()">
          <im-preview-icon class="exit-glyph" name="close" [size]="14" /><span class="exit-word">{{ 'shell.app_shell.exit' | transloco }}</span>
        </button>
      </div>
    }
  `,
  styleUrl: './delegation-pill.component.scss',
})
export class DelegationPillComponent {
  /** Off where a page draws its own acting-as strip (the schedule preview's takeover). */
  readonly suppressed = input(false);

  protected readonly delegation = inject(DelegationService);
  private readonly picker = inject(DelegatePickerService);
  private readonly analytics = inject(AnalyticsService);
  private readonly shell = inject(ShellStateService);

  protected readonly shown = computed(() => {
    const state = this.delegation.state();
    return state.active && state.delegate && !this.suppressed() ? state.delegate : null;
  });
  protected readonly initials = computed(() => getInitials(this.shown()?.name ?? ''));

  private readonly search = (): Element | null => document.querySelector(SEARCH);
  // Out of the search, down into place; back towards it on the way out.
  protected readonly arrive = enterMotion({ rise: -8, scale: 0.96, anchor: () => this.search() });
  protected readonly recede = leaveMotion({ scale: 0.96, anchor: () => this.search() });

  protected async switchPerson(): Promise<void> {
    if (this.picker.open()) return;
    this.track('switch');
    const person = await this.picker.pick();
    if (person) this.delegation.activateFor(person);
  }

  protected exit(): void {
    this.track('exit');
    this.delegation.deactivate();
  }

  private track(action: 'switch' | 'exit'): void {
    this.analytics.capture(ANALYTICS_EVENTS.DELEGATION_PILL_ACTION, { action, section: this.shell.currentSection() ?? 'unknown' });
  }
}
